import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Typography,
  Card,
  Row,
  Col,
  Alert,
  Divider,
  Spin,
  message
} from 'antd';
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  MailOutlined
} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../../../hooks/useAuth';
import { sanitizeTrustedHtml } from '../../../utils/sanitize';
import {
  useGetPendingResponseMonitoringMessagesQuery,
  useApproveResponseMonitoringMessageMutation,
  useRejectResponseMonitoringMessageMutation,
  useRegenerateResponseMonitoringMessageMutation,
  useSendTestEmailForResponseMonitoringMessageMutation
} from '../../../services/nestjs-base-api';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text, Paragraph } = Typography;

// LocalStorage key for persisting message changes
const SAVED_CHANGES_KEY = 'response-monitoring-saved-changes';

// Helper functions for localStorage persistence
const loadSavedChanges = (): {[key: number]: {subject: string; bodyHtml: string; bodyText: string}} | null => {
  try {
    const saved = localStorage.getItem(SAVED_CHANGES_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Failed to load saved changes from localStorage:', error);
    return null;
  }
};

const saveSavedChanges = (changes: {[key: number]: {subject: string; bodyHtml: string; bodyText: string}} | null) => {
  try {
    if (changes && Object.keys(changes).length > 0) {
      localStorage.setItem(SAVED_CHANGES_KEY, JSON.stringify(changes));
    } else {
      localStorage.removeItem(SAVED_CHANGES_KEY);
    }
  } catch (error) {
    console.error('Failed to save changes to localStorage:', error);
  }
};

// ReactQuill configuration for message editing
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'color': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link'],
    [{ 'align': [] }],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline',
  'color', 'list', 'bullet', 'indent',
  'link', 'align'
];

// Custom ReactQuill component for Ant Design Form integration
const MessageEditor: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  return (
    <ReactQuill
      theme="snow"
      value={value || ''}
      onChange={onChange}
      modules={quillModules}
      formats={quillFormats}
      placeholder={placeholder}
      style={{ height: '300px', marginBottom: '50px' }}
    />
  );
};

// Function to convert HTML to plain text
const htmlToPlainText = (html: string): string => {
  // Create a temporary div element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Convert to plain text with basic formatting
  let text = temp.textContent || temp.innerText || '';

  // Clean up extra whitespace and normalize line breaks
  text = text.replace(/\s+/g, ' ').trim();

  // Add line breaks for paragraphs and lists
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  let result = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;

      // Add line breaks for block elements
      if (['p', 'div', 'br'].includes(element.tagName?.toLowerCase())) {
        if (result && !result.endsWith('\n')) result += '\n';
      }

      // Add line breaks and formatting for lists
      if (element.tagName?.toLowerCase() === 'li') {
        result += '\n- ';
      }

      // Process children
      for (const child of Array.from(node.childNodes)) {
        walk(child);
      }

      // Add line breaks after block elements
      if (['p', 'div', 'li'].includes(element.tagName?.toLowerCase())) {
        if (!result.endsWith('\n')) result += '\n';
      }
    }
  };

  walk(doc.body);

  // Clean up multiple line breaks
  return result.replace(/\n{3,}/g, '\n\n').trim();
};

interface ResponseMonitoringMessage {
  id: number;
  hash: string;
  messageType: 'critical' | 'warning' | 'low';
  subject: string;
  bodyHtml: string;
  bodyText: string;
  brand: {
    id: number;
    name: string;
  };
  organisation: {
    id: number;
    name: string;
  };
  monitoringCheck: {
    responseCount: number;
    periodStart: string;
    periodEnd: string;
    thresholdLevel: string;
  };
  createdAt: string;
  tokensUsed?: number;
  generationAttempts: number;
}

interface PendingMessagesResponse {
  messages: ResponseMonitoringMessage[];
  total: number;
  page: number;
  totalPages: number;
}

export const PendingMessagesTab: React.FC = () => {
  const { user } = useAuth();
  const [selectedMessage, setSelectedMessage] = useState<ResponseMonitoringMessage | null>(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [testEmailModalVisible, setTestEmailModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [testEmailForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [savedChanges, setSavedChanges] = useState<{[key: number]: {subject: string; bodyHtml: string; bodyText: string}} | null>(null);
  const pageSize = 50;

  // Load saved changes from localStorage on component mount
  useEffect(() => {
    const loaded = loadSavedChanges();
    if (loaded) {
      setSavedChanges(loaded);
    }
  }, []);

  const { data: pendingData, isLoading, refetch } = useGetPendingResponseMonitoringMessagesQuery({
    jwtToken: user?.jwtToken || '',
    page: currentPage,
    limit: pageSize,
  });

  const [approveMessage, { isLoading: approving }] = useApproveResponseMonitoringMessageMutation();
  const [rejectMessage, { isLoading: rejecting }] = useRejectResponseMonitoringMessageMutation();
  const [regenerateMessage, { isLoading: regenerating }] = useRegenerateResponseMonitoringMessageMutation();
  const [sendTestEmail, { isLoading: sendingTestEmail }] = useSendTestEmailForResponseMonitoringMessageMutation();

  const getMessageTypeTag = (type: string) => {
    const config = {
      critical: { color: 'red', icon: <ExclamationCircleOutlined /> },
      warning: { color: 'orange', icon: <WarningOutlined /> },
      low: { color: 'blue', icon: <InfoCircleOutlined /> },
    };
    const { color, icon } = config[type as keyof typeof config] || config.low;
    return (
      <Tag color={color} icon={icon}>
        {type.toUpperCase()}
      </Tag>
    );
  };

  const handlePreview = (message: ResponseMonitoringMessage) => {
    setSelectedMessage(message);
    setPreviewModalVisible(true);
  };

  // Helper function to get the current content (saved changes or original)
  const getCurrentMessageContent = (message: ResponseMonitoringMessage) => {
    return savedChanges?.[message.id] || {
      subject: message.subject,
      bodyHtml: message.bodyHtml,
      bodyText: message.bodyText,
    };
  };

  const handleEdit = (message: ResponseMonitoringMessage) => {
    setSelectedMessage(message);

    // Use saved changes if they exist, otherwise use original message
    const currentContent = savedChanges?.[message.id] || {
      subject: message.subject,
      bodyHtml: message.bodyHtml,
      bodyText: message.bodyText,
    };

    editForm.setFieldsValue(currentContent);
    setEditModalVisible(true);
  };

  const handleApprove = async (messageId: number, editedContent?: any) => {
    try {
      // Use provided editedContent or saved changes
      const finalEditedContent = editedContent || savedChanges?.[messageId] || undefined;

      await approveMessage({
        jwtToken: user?.jwtToken || '',
        messageId,
        editedContent: finalEditedContent,
      }).unwrap();
      message.success('Message approved and sent successfully');

      // Clear saved changes for this message
      if (savedChanges?.[messageId]) {
        const newSavedChanges = { ...savedChanges };
        delete newSavedChanges[messageId];
        const finalChanges = Object.keys(newSavedChanges).length > 0 ? newSavedChanges : null;
        setSavedChanges(finalChanges);
        saveSavedChanges(finalChanges);
      }

      refetch();
      // Close any open modals
      setPreviewModalVisible(false);
      setEditModalVisible(false);
    } catch (error) {
      message.error('Failed to approve message');
    }
  };

  const handleReject = async (messageId: number) => {
    try {
      await rejectMessage({
        jwtToken: user?.jwtToken || '',
        messageId,
      }).unwrap();
      message.success('Message rejected');

      // Clear saved changes for this message
      if (savedChanges?.[messageId]) {
        const newSavedChanges = { ...savedChanges };
        delete newSavedChanges[messageId];
        const finalChanges = Object.keys(newSavedChanges).length > 0 ? newSavedChanges : null;
        setSavedChanges(finalChanges);
        saveSavedChanges(finalChanges);
      }

      refetch();
      // Close any open modals
      setPreviewModalVisible(false);
      setEditModalVisible(false);
    } catch (error) {
      message.error('Failed to reject message');
    }
  };

  const handleRegenerate = async (messageId: number) => {
    try {
      await regenerateMessage({
        jwtToken: user?.jwtToken || '',
        messageId,
      }).unwrap();
      message.success('Message regenerated');

      // Clear saved changes for this message since it's been regenerated
      if (savedChanges?.[messageId]) {
        const newSavedChanges = { ...savedChanges };
        delete newSavedChanges[messageId];
        const finalChanges = Object.keys(newSavedChanges).length > 0 ? newSavedChanges : null;
        setSavedChanges(finalChanges);
        saveSavedChanges(finalChanges);
      }

      refetch();
    } catch (error) {
      message.error('Failed to regenerate message');
    }
  };

  const handleTestEmail = (messageData: ResponseMonitoringMessage) => {
    setSelectedMessage(messageData);
    testEmailForm.resetFields();
    setTestEmailModalVisible(true);
  };

  const handleSendTestEmail = async () => {
    try {
      const values = await testEmailForm.validateFields();

      // Send the saved changes as test content if they exist
      const testContent = savedChanges?.[selectedMessage!.id] || undefined;

      await sendTestEmail({
        jwtToken: user?.jwtToken || '',
        messageId: selectedMessage!.id,
        testEmail: values.testEmail,
        testContent,
      }).unwrap();

      const contentType = testContent ? 'edited version' : 'original version';
      message.success(`Test email sent successfully (${contentType})`);
      setTestEmailModalVisible(false);
    } catch (error) {
      message.error('Failed to send test email');
    }
  };

  const handleSaveChanges = async () => {
    try {
      const values = await editForm.validateFields();

      // Auto-generate plain text from rich content
      const bodyHtml = values.bodyHtml || '';
      const bodyText = htmlToPlainText(bodyHtml);

      const editedContent = {
        subject: values.subject,
        bodyHtml,
        bodyText,
      };

      // Save changes locally and persist to localStorage
      const newSavedChanges = {
        ...savedChanges,
        [selectedMessage!.id]: editedContent
      };
      setSavedChanges(newSavedChanges);
      saveSavedChanges(newSavedChanges);

      message.success('Changes saved successfully');
      setEditModalVisible(false);
      setPreviewModalVisible(true);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const columns: ColumnsType<ResponseMonitoringMessage> = [
    {
      title: 'Brand',
      dataIndex: ['brand', 'name'],
      key: 'brandName',
      render: (name: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <Text type="secondary">{record.organisation.name}</Text>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'messageType',
      key: 'messageType',
      render: (type: string) => getMessageTypeTag(type),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (subject: string, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {savedChanges?.[record.id] ? getCurrentMessageContent(record).subject : subject}
            {savedChanges?.[record.id] && (
              <Tag color="orange">EDITED</Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Response Count',
      key: 'responseCount',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.monitoringCheck.responseCount}</div>
          <Text type="secondary">{record.monitoringCheck.thresholdLevel}</Text>
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          >
            Preview
          </Button>
          <Button
            size="small"
            icon={<MailOutlined />}
            loading={sendingTestEmail}
            onClick={() => handleTestEmail(record)}
          >
            Test Email
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={approving}
            onClick={() => handleApprove(record.id)}
          >
            Approve
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseOutlined />}
            loading={rejecting}
            onClick={() => handleReject(record.id)}
          >
            Reject
          </Button>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={regenerating}
            onClick={() => handleRegenerate(record.id)}
          >
            Regenerate
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: 40 }} />;
  }

  const pendingMessages = pendingData?.messages || [];

  return (
    <>
      {pendingMessages.length === 0 ? (
        <Alert
          message="No Pending Messages"
          description="There are currently no AI-generated messages waiting for approval."
          type="info"
          showIcon
        />
      ) : (
        <Table
          columns={columns}
          dataSource={pendingMessages}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: pendingData?.total || 0,
            onChange: setCurrentPage,
          }}
        />
      )}

      {/* Preview Modal */}
      <Modal
        title="Message Preview"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={700}
        footer={[
          <Button key="test" icon={<MailOutlined />} onClick={() => {
            setPreviewModalVisible(false);
            handleTestEmail(selectedMessage!);
          }}>
            Send Test Email
          </Button>,
          <Button key="edit" onClick={() => {
            setPreviewModalVisible(false);
            handleEdit(selectedMessage!);
          }}>
            Edit
          </Button>,
          <Button key="approve" type="primary" onClick={() => handleApprove(selectedMessage!.id)}>
            Approve & Send
          </Button>,
          <Button key="reject" danger onClick={() => handleReject(selectedMessage!.id)}>
            Reject
          </Button>,
        ]}
      >
        {selectedMessage && (
          <div>
            {savedChanges?.[selectedMessage.id] && (
              <Alert
                message="Viewing Edited Version"
                description="You are viewing the edited version with your saved changes. Use 'Edit' to modify further or 'Approve & Send' to send this version."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text strong>Brand:</Text> {selectedMessage.brand.name}
              </Col>
              <Col span={12}>
                <Text strong>Organization:</Text> {selectedMessage.organisation.name}
              </Col>
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text strong>Type:</Text> {getMessageTypeTag(selectedMessage.messageType)}
              </Col>
              <Col span={12}>
                <Text strong>Response Count:</Text> {selectedMessage.monitoringCheck.responseCount}
              </Col>
            </Row>
            <Divider />
            <Title level={4}>Subject</Title>
            <Paragraph>{getCurrentMessageContent(selectedMessage).subject}</Paragraph>
            <Title level={4}>Message Content</Title>
            <Card style={{ backgroundColor: '#fafafa' }}>
              {/* Sanitised like the sibling viewers in AdminMessagesPage and
                  Profile/Messages: this body is AI-drafted and editable, and it
                  renders in a platform-admin session. */}
              <div dangerouslySetInnerHTML={{ __html: sanitizeTrustedHtml(getCurrentMessageContent(selectedMessage).bodyHtml) }} />
            </Card>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Message"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveChanges}>
            Save Changes
          </Button>,
        ]}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Subject is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="bodyHtml"
            label="Message Content"
            rules={[{ required: true, message: 'Message content is required' }]}
            help="Edit your message using the rich text editor. Plain text version will be automatically generated."
          >
            <MessageEditor placeholder="Enter your message content..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Test Email Modal */}
      <Modal
        title="Send Test Email"
        open={testEmailModalVisible}
        onCancel={() => setTestEmailModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setTestEmailModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="send" type="primary" loading={sendingTestEmail} onClick={handleSendTestEmail}>
            Send Test Email
          </Button>,
        ]}
      >
        <Form form={testEmailForm} layout="vertical">
          <Alert
            message="Test Email Preview"
            description={
              savedChanges?.[selectedMessage?.id || 0]
                ? "This will send your edited version to the specified email address with a [TEST] prefix and warning banner."
                : "This will send the original message to the specified email address with a [TEST] prefix and warning banner."
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            name="testEmail"
            label="Test Email Address"
            rules={[
              { required: true, message: 'Email address is required' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input
              placeholder="Enter email address to send test to..."
              suffix={<MailOutlined />}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};